import { Vector3D } from '@awayjs/core';
import { KeyboardEvent } from '../events/KeyboardEvent';
import { MouseEvent as AwayMouseEvent } from '../events/MouseEvent';
import { FrameScriptManager } from '../managers/FrameScriptManager';
/**
 * MouseManager enforces a singleton pattern and is not intended to be instanced.
 * it provides a manager class for detecting mouse hits on scene objects and sending out mouse events.
 */
var MouseManager = /** @class */ (function () {
    /**
     * Creates a new <code>MouseManager</code> object.
     */
    function MouseManager(stage) {
        this._pickerLookup = new Array();
        this.allowKeyInput = true;
        this._collisionIsEnabledButton = false;
        this._eventBubbling = true; //  should events bubble up
        this._allowFocusOnUnfocusable = true; // should unfocus-able object steal focus ?
        this._nullVector = new Vector3D();
        this._queuedEvents = new Array();
        this._mouseUp = new AwayMouseEvent(AwayMouseEvent.MOUSE_UP);
        this._mouseUpOutside = new AwayMouseEvent(AwayMouseEvent.MOUSE_UP_OUTSIDE);
        this._mouseClick = new AwayMouseEvent(AwayMouseEvent.CLICK);
        this._mouseOut = new AwayMouseEvent(AwayMouseEvent.MOUSE_OUT);
        this._dragOut = new AwayMouseEvent(AwayMouseEvent.DRAG_OUT);
        this._dragOver = new AwayMouseEvent(AwayMouseEvent.DRAG_OVER);
        this._mouseDown = new AwayMouseEvent(AwayMouseEvent.MOUSE_DOWN);
        this._mouseMove = new AwayMouseEvent(AwayMouseEvent.MOUSE_MOVE);
        this._mouseOver = new AwayMouseEvent(AwayMouseEvent.MOUSE_OVER);
        this._mouseWheel = new AwayMouseEvent(AwayMouseEvent.MOUSE_WHEEL);
        this._mouseDoubleClick = new AwayMouseEvent(AwayMouseEvent.DOUBLE_CLICK);
        this._rollOver = new AwayMouseEvent(AwayMouseEvent.ROLL_OVER);
        this._rollOut = new AwayMouseEvent(AwayMouseEvent.ROLL_OUT);
        this._dragMove = new AwayMouseEvent(AwayMouseEvent.DRAG_MOVE);
        this._dragStart = new AwayMouseEvent(AwayMouseEvent.DRAG_START);
        this._dragStop = new AwayMouseEvent(AwayMouseEvent.DRAG_STOP);
        this._useSoftkeyboard = false;
        this._isAVM1Dragging = false;
        this._isDown = false;
        this._stage = stage;
        this.onClick = this.onClick.bind(this);
        this.onDoubleClick = this.onDoubleClick.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onMouseWheel = this.onMouseWheel.bind(this);
        this.onMouseOver = this.onMouseOver.bind(this);
        this.onMouseOut = this.onMouseOut.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.buttonEnabledDirty = false;
        this._isTouch = (('ontouchstart' in self) || navigator.msMaxTouchPoints > 0);
        this._showCursor = true;
        this._mouseDragging = false;
        //register stage
        var container = this._stage.container;
        container.addEventListener('click', this.onClick);
        container.addEventListener('dblclick', this.onClick);
        container.addEventListener('touchstart', this.onMouseDown);
        container.addEventListener('mousedown', this.onMouseDown);
        window.addEventListener('touchmove', this.onMouseMove);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
        container.addEventListener('touchend', this.onMouseUp);
        container.addEventListener('touchend', this.onClick);
        container.addEventListener('mousewheel', this.onMouseWheel);
        container.addEventListener('mouseover', this.onMouseOver);
        container.addEventListener('mouseout', this.onMouseOut);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        // supress context menu
        window.addEventListener('contextmenu', function (e) { return e.preventDefault(); });
    }
    MouseManager.prototype.startDragObject = function (collision) {
        this._isAVM1Dragging = true;
        if (!this._mouseDragCollision)
            this._mouseDragCollision = collision;
    };
    MouseManager.prototype.stopDragObject = function () {
        this._isAVM1Dragging = false;
    };
    Object.defineProperty(MouseManager.prototype, "showCursor", {
        get: function () {
            return this._showCursor;
        },
        set: function (value) {
            this._showCursor = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MouseManager.prototype, "eventBubbling", {
        get: function () {
            return this._eventBubbling;
        },
        set: function (value) {
            this._eventBubbling = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MouseManager.prototype, "useSoftkeyboard", {
        get: function () {
            return this._useSoftkeyboard;
        },
        set: function (value) {
            this._useSoftkeyboard = value;
            // todo: improve this, so that we can
            //	- use device-softkeyboard on tablet / mobide
            //	- optionally use a custom softkeyboard
            if (!value) {
                window.addEventListener('keydown', this.onKeyDown);
                window.addEventListener('keyup', this.onKeyUp);
            }
            else if (value) {
                window.removeEventListener('keydown', this.onKeyDown);
                window.removeEventListener('keyup', this.onKeyUp);
            }
        },
        enumerable: true,
        configurable: true
    });
    MouseManager.prototype.dispose = function () {
        //unregister stage
        var container = this._stage.container;
        container.removeEventListener('click', this.onClick);
        container.removeEventListener('dblclick', this.onDoubleClick);
        container.removeEventListener('touchstart', this.onMouseDown);
        container.removeEventListener('mousedown', this.onMouseDown);
        window.removeEventListener('touchmove', this.onMouseMove);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        container.removeEventListener('touchend', this.onMouseUp);
        container.removeEventListener('touchend', this.onClick);
        container.removeEventListener('wheel', this.onMouseWheel);
        container.removeEventListener('mouseover', this.onMouseOver);
        container.removeEventListener('mouseout', this.onMouseOut);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        this._stage = null;
        this._mouseMoveEvent = null;
        this._mouseUp = null;
        this._mouseUpOutside = null;
        this._mouseClick = null;
        this._mouseOut = null;
        this._dragOut = null;
        this._dragOver = null;
        this._mouseDown = null;
        this._mouseMove = null;
        this._mouseOver = null;
        this._mouseWheel = null;
        this._mouseDoubleClick = null;
        this._dragMove = null;
        this._dragStart = null;
        this._dragStop = null;
    };
    MouseManager.clearInstance = function (stage) {
        if (this._instancePool[stage.id]) {
            this._instancePool[stage.id].dispose();
            delete this._instancePool[stage.id];
        }
    };
    MouseManager.getInstance = function (stage) {
        return this._instancePool[stage.id]
            || (this._instancePool[stage.id] = new MouseManager(stage));
    };
    MouseManager.prototype.setFocus = function (obj) {
        if (this._focusContainer == obj) {
            return;
        }
        if (this._focusContainer) {
            this._focusContainer.setFocus(false, false);
        }
        this._focusContainer = obj;
        if (this._focusContainer) {
            this._focusContainer.setFocus(true, false);
        }
    };
    MouseManager.prototype.getFocus = function () {
        return this._focusContainer;
    };
    MouseManager.prototype.dispatchEvent = function (event, dispatcher) {
        if (!this._eventBubbling) {
            if (dispatcher) {
                dispatcher.container.dispatchEvent(event);
                FrameScriptManager.execute_queue();
            }
            return;
        }
        while (dispatcher) {
            if (event.commonAncestor && dispatcher == event.commonAncestor) {
                return;
            }
            if (!dispatcher.isMouseDisabled()) {
                dispatcher.container.dispatchEvent(event);
                FrameScriptManager.execute_queue();
            }
            if (!event._iAllowedToPropagate) {
                dispatcher = null;
            }
            else {
                dispatcher = dispatcher.parent;
            }
        }
    };
    MouseManager.prototype.setupAndDispatchEvent = function (event, sourceEvent, collision, commonAncestor) {
        if (commonAncestor === void 0) { commonAncestor = null; }
        if (sourceEvent) {
            event.delta = sourceEvent.wheelDelta;
            event.ctrlKey = sourceEvent.ctrlKey;
            event.altKey = sourceEvent.altKey;
            event.shiftKey = sourceEvent.shiftKey;
        }
        event = this.setUpEvent(event, collision, commonAncestor);
        this.dispatchEvent(event, event.rootNode);
    };
    MouseManager.prototype.fireMouseEvents = function (forcePicker) {
        if (forcePicker === void 0) { forcePicker = null; }
        var _a, _b, _c, _d, _e;
        var collision;
        if (forcePicker) {
            collision = forcePicker.getViewCollision(this._stage.screenX, this._stage.screenY);
        }
        else if (this._updateDirty) {
            for (var i = 0; i < this._pickerLookup.length; i++)
                if (!collision || this._pickerLookup[i].layeredView)
                    collision = this._pickerLookup[i].getViewCollision(this._stage.screenX, this._stage.screenY, false, collision);
        }
        //if theres nothing to update, return
        if (!forcePicker && !this._updateDirty)
            return;
        var event;
        var dispatcher;
        var len = this._queuedEvents.length;
        // Dispatch all queued events.
        /*var logEvents="";
        for (var i: number = 0; i < len; ++i) {
            logEvents+= " / "+this._queuedEvents[i].type;
        }
        console.log(logEvents);*/
        for (var i = 0; i < len; ++i) {
            event = this._queuedEvents[i];
            dispatcher = (_a = collision) === null || _a === void 0 ? void 0 : _a.rootNode;
            this.setUpEvent(event, collision);
            if (event.type == AwayMouseEvent.MOUSE_DOWN) {
                this._mouseDragging = true;
                // no event-bubbling. dispatch on stage first
                if (!this._eventBubbling)
                    this._stage.dispatchEvent(event);
                // @todo: at this point the object under the mouse might have been changed,
                // so we need to recheck the collision ?
                // on Touch dispatch mouseOver Command
                if (this._isTouch)
                    this.setupAndDispatchEvent(this._mouseOver, this._mouseMoveEvent, collision);
                this._mouseDragCollision = collision;
                if (dispatcher) {
                    //console.log("onPress", dispatcher)
                    this.dispatchEvent(event, dispatcher);
                }
                else if (this._eventBubbling)
                    this._stage.dispatchEvent(event);
                //  in FP6, a mouseclick on non focus-able object still steal the focus
                //  in newer FP they only steal the focus if the the new hit is focusable
                if (this._allowFocusOnUnfocusable || ((_b = this._mouseDragCollision) === null || _b === void 0 ? void 0 : _b.rootNode.container.tabEnabled)) {
                    if (this._focusContainer)
                        this._focusContainer.setFocus(false, true);
                    this._focusContainer = (_c = this._mouseDragCollision) === null || _c === void 0 ? void 0 : _c.rootNode.container;
                    if (this._focusContainer)
                        this._focusContainer.setFocus(true, true);
                }
                if (this._mouseDragCollision)
                    this.setupAndDispatchEvent(this._dragStart, event, this._mouseDragCollision);
            }
            else if (event.type == AwayMouseEvent.MOUSE_UP) {
                // no event-bubbling. dispatch on stage first
                if (!this._eventBubbling)
                    this._stage.dispatchEvent(event);
                // @todo: at this point the object under the mouse might have been changed,
                // so we need to recheck the collision ?
                var upEntityNode = null;
                var upRootNode = null;
                if (this._isAVM1Dragging && this._mouseDragCollision) {
                    // avm1dragging is in process, dispatch the mouse-up on this.
                    // mouseDragEntity instead of the current collision
                    upRootNode = this._mouseDragCollision.rootNode;
                    upEntityNode = this._mouseDragCollision.entityNode;
                }
                else if (this._mouseDragging && this._mouseDragCollision
                    && this._mouseDragCollision.rootNode != dispatcher) {
                    // no avm1dragging is in process, but current collision
                    // is not the same as collision that appeared on mouse-down,
                    // need to dispatch a MOUSE_UP_OUTSIDE on _mouseDragEntity
                    if (this._mouseDragCollision.rootNode.buttonEnabled)
                        this.setupAndDispatchEvent(this._mouseOut, event, this._mouseDragCollision);
                    if (!this._eventBubbling) {
                        this.setupAndDispatchEvent(this._mouseUpOutside, event, this._mouseDragCollision);
                    }
                }
                else if (this._mouseDragging && this._mouseDragCollision
                    && this._mouseDragCollision.rootNode == dispatcher) {
                    // no avm1dragging is in process,
                    // but current collision is not the same as collision that appeared on mouse-down,
                    // need to dispatch a MOUSE_UP_OUTSIDE on _mouseDragEntity
                    upRootNode = this._mouseDragCollision.rootNode;
                    upEntityNode = this._mouseDragCollision.entityNode;
                }
                if (this._mouseDragging && dispatcher)
                    this.setupAndDispatchEvent(this._mouseOver, event, collision);
                if (this._isTouch && upEntityNode)
                    this.setupAndDispatchEvent(this._mouseOut, this._mouseMoveEvent, this._mouseDragCollision);
                if (upRootNode) {
                    //console.log("onRelease", upRootNode)
                    this.dispatchEvent(event, upRootNode);
                }
                else if (this._eventBubbling && dispatcher)
                    this.dispatchEvent(event, dispatcher);
                else if (this._eventBubbling)
                    this._stage.dispatchEvent(event);
                if (upEntityNode)
                    this.setupAndDispatchEvent(this._dragStop, event, this._mouseDragCollision);
                this._mouseDragCollision = null;
                this._mouseDragging = false;
                this._isAVM1Dragging = false;
            }
            else if (event.type == AwayMouseEvent.MOUSE_MOVE) {
                // no event-bubbling. dispatch on stage first
                if (!this._eventBubbling) {
                    this._stage.dispatchEvent(event);
                }
                // fire to picker
                if (event.rootNode) {
                    this.dispatchEvent(event, event.rootNode);
                }
                if (this._mouseDragCollision) {
                    this.setupAndDispatchEvent(this._dragMove, event, this._mouseDragCollision);
                }
                // if bubling is exist, fire to stage late
                if (this._eventBubbling) {
                    this._stage.dispatchEvent(event);
                }
            }
            else {
                // MouseEvent.MOUSE_OVER / MouseEvent.MOUSE_OUT / MouseEvent.DRAG_OVER / MouseEvent.DRAG_OUT
                this.dispatchEvent(event, dispatcher);
            }
        }
        this._queuedEvents.length = 0;
        this._rollOut.commonAncestor = null;
        this._rollOver.commonAncestor = null;
        var collisionNode = (_d = collision) === null || _d === void 0 ? void 0 : _d.rootNode;
        var prevCollisionNode = (_e = this._prevCollision) === null || _e === void 0 ? void 0 : _e.rootNode;
        if (collisionNode != prevCollisionNode) {
            //  If colliding object has changed, queue OVER and OUT events.
            //  If the mouse is dragged (mouse-down is hold), use DRAG_OVER and DRAG_OUT instead of MOUSE_OVER MOUSE_OUT
            //  DRAG_OVER and DRAG_OUT are only dispatched on the object that was hit on the mouse-down
            // (_mouseDragEntity)
            //  Store the info if the collision is a enabled Button (_collisionIsEnabledButton)
            if (prevCollisionNode) {
                if (!this._isTouch && !this._mouseDragging)
                    this.setupAndDispatchEvent(this._mouseOut, this._mouseMoveEvent, this._prevCollision);
                else if (this._mouseDragging && this._mouseDragCollision
                    && this._mouseDragCollision.rootNode == prevCollisionNode)
                    this.setupAndDispatchEvent(this._dragOut, this._mouseMoveEvent, this._prevCollision);
            }
            if (!prevCollisionNode && collisionNode) {
                // rollout / rollover easy case, can just bubble up
                this.setupAndDispatchEvent(this._rollOut, this._mouseMoveEvent, this._prevCollision);
                this.setupAndDispatchEvent(this._rollOver, this._mouseMoveEvent, collision);
            }
            if (prevCollisionNode && !collisionNode) {
                // rollout / rollover easy case, can just bubble up
                this.setupAndDispatchEvent(this._rollOut, this._mouseMoveEvent, this._prevCollision);
                this.setupAndDispatchEvent(this._rollOver, this._mouseMoveEvent, collision);
            }
            if (prevCollisionNode && collisionNode) {
                // rollout / rollover find common ancester and only bubble up to that point
                var parentsPrev = [];
                var parent_1 = prevCollisionNode;
                while (parent_1 && !parent_1.container.isAVMScene) {
                    parentsPrev.push(parent_1);
                    parent_1 = parent_1.parent;
                }
                var commonAncestor = null;
                parent_1 = collisionNode;
                while (parent_1 && !parent_1.container.isAVMScene) {
                    var oldParentIdx = parentsPrev.indexOf(parent_1);
                    if (oldParentIdx == -1) {
                        parent_1 = parent_1.parent;
                    }
                    else {
                        commonAncestor = parent_1;
                        parent_1 = null;
                    }
                }
                if (commonAncestor != prevCollisionNode)
                    this.setupAndDispatchEvent(this._rollOut, this._mouseMoveEvent, this._prevCollision, commonAncestor);
                if (commonAncestor != collisionNode)
                    this.setupAndDispatchEvent(this._rollOver, this._mouseMoveEvent, collision, commonAncestor);
            }
            this._collisionIsEnabledButton = collisionNode ? collisionNode.buttonEnabled : false;
            if (collisionNode) {
                if (!this._isTouch && !this._mouseDragging)
                    this.setupAndDispatchEvent(this._mouseOver, this._mouseMoveEvent, collision);
                else if (this._mouseDragging && this._mouseDragCollision &&
                    this._mouseDragCollision.rootNode == collisionNode)
                    this.setupAndDispatchEvent(this._dragOver, this._mouseMoveEvent, collision);
            }
            this._prevCollision = collision;
        }
        else {
            //  colliding object has not changed
            //  Check if we need to send any MOUSE_OVER/DRAG_OVER event to handle the case
            //  when a Button has become active while under the mouse.
            var isActiveButton = collisionNode ? collisionNode.buttonEnabled : false;
            if (this._collisionIsEnabledButton != isActiveButton && isActiveButton) {
                if (!this._isTouch)
                    this.setupAndDispatchEvent(this._mouseOver, this._mouseMoveEvent, collision);
                else if (this._mouseDragCollision && this._mouseDragCollision.rootNode == collisionNode)
                    this.setupAndDispatchEvent(this._dragOver, this._mouseMoveEvent, collision);
            }
            this._collisionIsEnabledButton = isActiveButton;
        }
        // set cursor if not dragging mouse
        if (!this._mouseDragging)
            document.body.style.cursor =
                this._showCursor
                    ? (collisionNode ? collisionNode.container.getMouseCursor() : 'initial')
                    : 'none';
        this._updateDirty = false;
    };
    //		public addSceneLayer(scene:Scene)
    //		{
    //			var stg:Stage = scene.stage;
    //
    //			// Add instance to mouse3dmanager to fire mouse events for multiple scenes
    //			if (!scene.stageGL.mouse3DManager)
    //				scene.stageGL.mouse3DManager = this;
    //
    //			if (!hasKey(scene))
    //				_scene3Ds[scene] = 0;
    //
    //			_childDepth = 0;
    //			traverseDisplayObjects(stg);
    //			_sceneCount = _childDepth;
    //		}
    MouseManager.prototype.registerPicker = function (picker) {
        this._pickerLookup.push(picker);
    };
    MouseManager.prototype.unregisterPicker = function (picker) {
        this._pickerLookup.splice(this._pickerLookup.indexOf(picker), 1);
    };
    MouseManager.prototype.addEventsForSceneBinary = function (touchMessage, sceneIdx) {
        if (sceneIdx === void 0) { sceneIdx = 0; }
        var newTouchEvent = {};
        newTouchEvent.clientX = null; // we get the x position from the active touch
        newTouchEvent.clientY = null; // we get the y position from the active touch
        newTouchEvent.touches = [];
        newTouchEvent.changedTouches = [];
        newTouchEvent.preventDefault = function () { };
        var messageScene = new Float32Array(touchMessage);
        // transfer touches to event
        var i = 0;
        var cnt = 0;
        var touchCnt = 0;
        cnt++; //we temporary added 1 float to transfer fps from java to js. skip this
        var numTouches = messageScene[cnt++];
        var touchtype = messageScene[cnt++];
        var activeTouchID = messageScene[cnt++];
        if ((touchtype != 1) && (touchtype != 6) && (touchtype != 12) && (touchtype != 262) && (touchtype != 518)) {
            // if this is not a UP command, we add all touches
            for (i = 0; i < numTouches; i++) {
                var newTouch = {};
                newTouch.identifier = messageScene[cnt++];
                newTouch.clientX = messageScene[cnt++];
                newTouch.clientY = messageScene[cnt++];
                newTouchEvent.touches[i] = newTouch;
                //newTouchEvent.changedTouches[i] = newTouch;
            }
            newTouchEvent.changedTouches[0] = newTouchEvent.touches[activeTouchID];
        }
        else {
            // if this is a UP command, we add all touches, except the active one
            if (numTouches == 1) {
                var newTouch = {};
                newTouch.identifier = messageScene[cnt++];
                newTouch.clientX = messageScene[cnt++];
                newTouch.clientY = messageScene[cnt++];
                newTouchEvent.clientX = newTouch.clientX;
                newTouchEvent.clientY = newTouch.clientY;
            }
            else {
                for (i = 0; i < numTouches; i++) {
                    var newTouch = {};
                    newTouch.identifier = messageScene[cnt++];
                    newTouch.clientX = messageScene[cnt++];
                    newTouch.clientY = messageScene[cnt++];
                    if (i != activeTouchID) {
                        newTouchEvent.touches[touchCnt] = newTouch;
                        //newTouchEvent.changedTouches[touchCnt++] = newTouch;
                    }
                    else {
                        newTouchEvent.clientX = newTouch.clientX;
                        newTouchEvent.clientY = newTouch.clientY;
                    }
                }
            }
        }
        //console.log("Touch ID:"+touchtype+" activeTouchID "+activeTouchID+" numTouches "+numTouches+" x"+x+" y"+y);
        /*
         public static final int ACTION_DOWN = 0;
         public static final int ACTION_POINTER_1_DOWN = 5;
         public static final int ACTION_POINTER_DOWN = 5;
         public static final int ACTION_BUTTON_PRESS = 11;
         public static final int ACTION_POINTER_2_DOWN = 261;
         public static final int ACTION_POINTER_3_DOWN = 517;

         public static final int ACTION_UP = 1;
         public static final int ACTION_POINTER_1_UP = 6;
         public static final int ACTION_POINTER_UP = 6;
         public static final int ACTION_BUTTON_RELEASE = 12;
         public static final int ACTION_POINTER_2_UP = 262;
         public static final int ACTION_POINTER_3_UP = 518;

         public static final int ACTION_MOVE = 2;

         */
        if ((touchtype == 0) || (touchtype == 5) || (touchtype == 11) || (touchtype == 261) || (touchtype == 517)) {
            this.onMouseDown(newTouchEvent);
        }
        else if ((touchtype == 1) || (touchtype == 6)
            || (touchtype == 12) || (touchtype == 262) || (touchtype == 518)) {
            this.onMouseUp(newTouchEvent);
        }
        else if (touchtype == 2) {
            this.onMouseMove(newTouchEvent);
        }
        else {
            console.log('recieved unknown touch event-type: ' + touchtype);
        }
    };
    MouseManager.prototype.fireEventsForSceneFromString = function (touchMessage, _sceneIdx) {
        if (_sceneIdx === void 0) { _sceneIdx = 0; }
        var newTouchEvent = {};
        newTouchEvent.clientX = null; // set the x position from the active touch
        newTouchEvent.clientY = null; // set the y position from the active touch
        newTouchEvent.preventDefault = function () { };
        var touchesFromMessage = touchMessage.split(',');
        // transfer touches to event
        var i = 0;
        var cnt = 0;
        var numTouches = parseInt(touchesFromMessage[cnt++]);
        var touchtype = parseInt(touchesFromMessage[cnt++]);
        var activeTouch = parseInt(touchesFromMessage[cnt++]);
        newTouchEvent.touches = [];
        newTouchEvent.changedTouches = [];
        if ((touchtype != 1) && (touchtype != 6)) {
            for (i = 0; i < numTouches; i++) {
                var newTouch = {};
                newTouch.identifier = touchesFromMessage[cnt++];
                newTouch.clientX = touchesFromMessage[cnt++];
                newTouch.clientY = touchesFromMessage[cnt++];
                newTouchEvent.touches[i] = newTouch;
                newTouchEvent.changedTouches[i] = newTouch;
            }
            newTouchEvent.changedTouches[i] = newTouchEvent.touches[activeTouch];
        }
        else {
            for (i = 0; i < numTouches; i++) {
                if (i != activeTouch) {
                    var newTouch = {};
                    newTouch.identifier = touchesFromMessage[cnt++];
                    newTouch.clientX = touchesFromMessage[cnt++];
                    newTouch.clientY = touchesFromMessage[cnt++];
                    newTouchEvent.touches[i] = newTouch;
                    newTouchEvent.changedTouches[i] = newTouch;
                }
                else {
                    newTouchEvent.clientX = touchesFromMessage[cnt++];
                    newTouchEvent.clientY = touchesFromMessage[cnt++];
                    cnt++;
                }
            }
        }
        if (touchtype == 0) { //mousedown
            this.onMouseDown(newTouchEvent);
        }
        else if (touchtype == 1) { //mouseup
            this.onMouseUp(newTouchEvent);
        }
        else if (touchtype == 2) { //mousemove
            this.onMouseMove(newTouchEvent);
        }
        else if (touchtype == 261) { //mousedownPointer
            this.onMouseDown(newTouchEvent);
        }
        else if (touchtype == 6) { //mouseupPointer
            this.onMouseUp(newTouchEvent);
        }
    };
    // ---------------------------------------------------------------------
    // Private.
    // ---------------------------------------------------------------------
    MouseManager.prototype.setUpEvent = function (event, collision, commonAncestor) {
        if (commonAncestor === void 0) { commonAncestor = null; }
        var _a, _b;
        event._iAllowedToImmediatlyPropagate = true;
        event._iAllowedToPropagate = true;
        // 2D properties.
        event.screenX = this._stage.screenX;
        event.screenY = this._stage.screenY;
        //console.log("event", event, collisionNode, collisionNode);
        // 3D properties.
        if (collision) {
            event.entityNode = collision.entityNode;
            event.rootNode = collision.rootNode;
            // Object.
            event.traversable = collision.traversable;
            // UV.
            event.uv = collision.uv;
            // Position.
            event.position = (_a = collision.position) === null || _a === void 0 ? void 0 : _a.clone();
            // Normal.
            event.normal = (_b = collision.normal) === null || _b === void 0 ? void 0 : _b.clone();
            // Face index.
            event.elementIndex = collision.elementIndex;
        }
        else {
            // Set all to null.
            event.entityNode = null;
            event.rootNode = null;
            event.traversable = null;
            event.uv = null;
            event.position = this._nullVector;
            event.normal = this._nullVector;
            event.elementIndex = 0;
        }
        event.commonAncestor = commonAncestor;
        return event;
    };
    MouseManager.prototype.queueDispatch = function (event, sourceEvent) {
        // Store event to be dispatched later.
        event.delta = (sourceEvent instanceof WheelEvent) ? sourceEvent.deltaY : 0;
        event.ctrlKey = sourceEvent.ctrlKey;
        event.altKey = sourceEvent.altKey;
        event.shiftKey = sourceEvent.shiftKey;
        event.button = (sourceEvent instanceof MouseEvent) ? sourceEvent.button : 0;
        this._queuedEvents.push(event);
    };
    // ---------------------------------------------------------------------
    // Listeners.
    // ---------------------------------------------------------------------
    MouseManager.prototype.onKeyDown = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        //console.log("Keydown", event);
        if (this.allowKeyInput) {
            event.preventDefault();
            if (this._focusContainer || this._stage) {
                //console.log("dispatch keydown on ", this._focusContainer);
                var newEvent = new KeyboardEvent(KeyboardEvent.KEYDOWN, event.key, event.code);
                newEvent.isShift = event.shiftKey;
                newEvent.isCTRL = event.ctrlKey;
                newEvent.isAlt = event.altKey;
                newEvent.keyCode = event.keyCode;
                if (this._focusContainer)
                    this._focusContainer.dispatchEvent(newEvent);
                if (this._stage)
                    this._stage.dispatchEvent(newEvent);
            }
        }
    };
    MouseManager.prototype.onKeyUp = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        //console.log("Keyup", event);
        if (this.allowKeyInput) {
            event.preventDefault();
            if (this._focusContainer || this._stage) {
                //console.log("dispatch keydown on ", this._focusContainer);
                var newEvent = new KeyboardEvent(KeyboardEvent.KEYUP, event.key, event.code);
                newEvent.isShift = event.shiftKey;
                newEvent.isCTRL = event.ctrlKey;
                newEvent.isAlt = event.altKey;
                newEvent.keyCode = event.keyCode;
                if (this._focusContainer)
                    this._focusContainer.dispatchEvent(newEvent);
                if (this._stage)
                    this._stage.dispatchEvent(newEvent);
            }
        }
    };
    MouseManager.prototype.onMouseMove = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this._isTouch = (event.type != 'mousemove');
        this.updateColliders(event);
        this.queueDispatch(this._mouseMove, this._mouseMoveEvent = event);
    };
    MouseManager.prototype.onMouseOut = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this.updateColliders(event);
        this.queueDispatch(this._mouseOut, event);
    };
    MouseManager.prototype.onMouseOver = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this.updateColliders(event);
        this.queueDispatch(this._mouseOver, event);
    };
    MouseManager.prototype.onClick = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this.updateColliders(event);
        this.queueDispatch(this._mouseClick, event);
    };
    MouseManager.prototype.onDoubleClick = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this.updateColliders(event);
        // prevent middle and second button
        if (event instanceof MouseEvent && event.buttons & 6) {
            event.preventDefault();
        }
        this.queueDispatch(this._mouseDoubleClick, event);
    };
    MouseManager.prototype.onMouseDown = function (event) {
        this._isTouch = (event.type !== 'mousedown');
        if (this._isDown) {
            return;
        }
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this._isDown = true;
        this.updateColliders(event);
        var prevent = this._isTouch;
        // prevent middle and second button
        if (event instanceof MouseEvent && event.buttons & 6) {
            prevent = true;
        }
        if (prevent) {
            event.preventDefault();
            this._stage.container.focus();
        }
        this.queueDispatch(this._mouseDown, event);
    };
    MouseManager.prototype.onMouseUp = function (event) {
        if (!this._isDown) {
            return;
        }
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this._isDown = false;
        this.updateColliders(event);
        var prevent = this._isTouch;
        // prevent middle and second button
        if (event instanceof MouseEvent && event.button === 1) {
            prevent = true;
        }
        if (prevent) {
            event.preventDefault();
            this._stage.container.focus();
        }
        this.queueDispatch(this._mouseUp, event);
    };
    MouseManager.prototype.onMouseWheel = function (event) {
        !MouseManager.inputRecorder || MouseManager.inputRecorder.recordEvent(event);
        this.updateColliders(event);
        this.queueDispatch(this._mouseWheel, event);
    };
    MouseManager.prototype.updateColliders = function (event) {
        this._stage.interactionHandler(event);
        this._updateDirty = true;
    };
    MouseManager._instancePool = {};
    return MouseManager;
}());
export { MouseManager };
